import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { graphql } from "@src/gql/gql";
import { NavbarSection } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const navbarUnfavouriteMutation = graphql(/* GraphQL */ `
	mutation navbarUnfavourite($itemKey: String!) {
		navbar {
			unfavourite(itemKey: $itemKey)
		}
	}
`);

type NavbarUnfavouriteInput = {
	itemKey: string;
};

export function useNavbarUnfavourite() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ itemKey }: NavbarUnfavouriteInput) => {
			const response = await client.mutate({
				mutation: navbarUnfavouriteMutation,
				variables: { itemKey },
			});
			return response.data?.navbar?.unfavourite ?? false;
		},
		onMutate: async ({ itemKey }) => {
			const favQueryKey = navbarBranchQueryKeys.branch(
				NavbarSection.Favourites,
				null,
			);
			await queryClient.cancelQueries({ queryKey: favQueryKey });
			const previousFavourites =
				queryClient.getQueryData<NavbarBranchItem[]>(favQueryKey);

			queryClient.setQueryData<NavbarBranchItem[]>(favQueryKey, (old) => {
				if (!old) {
					return old;
				}
				return old.filter((item) => item.key !== itemKey);
			});

			return { previousFavourites, favQueryKey };
		},
		onError: (error, _variables, context) => {
			if (context?.favQueryKey) {
				queryClient.setQueryData(
					context.favQueryKey,
					context.previousFavourites,
				);
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, _vars, context) => {
			if (context?.favQueryKey) {
				queryClient.invalidateQueries({ queryKey: context.favQueryKey });
			}
		},
	});
}
